import { Directive, ElementRef, HostListener, Input, OnDestroy, inject } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ShortcutTooltipPanelComponent } from './shortcut-tooltip-panel.component';
import type { NavigationInfo } from '@services/core-services/shortcuts.constants';

@Directive({
  standalone: true,
  selector: '[appShortcutTooltip]',
})
export class CeShortcutTooltipDirective implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private overlayRef: OverlayRef | null = null;

  @Input('appShortcutTooltip') info: NavigationInfo | null = null;
  @Input() appShortcutTooltipKeysOnly: boolean = false;

  @HostListener('focus') onFocus() {
    if (!this.info) return;
    this.showTooltip();
  }

  @HostListener('blur') onBlur() {
    this.detach();
  }

  @HostListener('mouseenter') onEnter() {
    if (!this.info) return;
    this.showTooltip();
  }

  @HostListener('mouseleave') onLeave() {
    this.detach();
  }

  @HostListener('click') onClick() {
    this.detach(true);
  }

  ngOnDestroy(): void {
    this.detach(true);
  }

  private showTooltip() {
    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.host)
        .withPositions([
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 8,
          },
        ]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: ['mat-tooltip-panel', 'custom-shortcut-tooltip'],
      });
    }

    const portal = new ComponentPortal(ShortcutTooltipPanelComponent);
    const ref = this.overlayRef!.attach(portal);
    ref.instance.info = this.info;
    ref.instance.showKeysOnly = this.appShortcutTooltipKeysOnly;
  }

  private detach(dispose = false) {
    if (this.overlayRef) {
      this.overlayRef.detach();
      if (dispose) {
        this.overlayRef.dispose();
        this.overlayRef = null;
      }
    }
  }
}
